import { DEFAULT_DATE_FORMAT, DEFAULT_TIME_FORMAT, extractDateFromISO, extractTimeFromISO } from '@app/booking-libs';
import { Box, DatePicker, Inline, TimePicker, xcss } from '@forge/react';
import React, { useEffect, useReducer } from 'react';
function merge(date, time) {
    if (!(date && time)) {
        return undefined;
    }
    return `${date}T${time}`;
}
function reducer(state, action) {
    switch (action.type) {
        case 'date': {
            const date = action.payload.date || '';
            return {
                ...state,
                date,
                datetime: merge(date, state.time)
            };
        }
        case 'time': {
            const time = action.payload.time || '';
            return {
                ...state,
                time,
                datetime: merge(state.date, time)
            };
        }
        case 'reset': {
            const date = action.payload.date || '';
            const time = action.payload.time || '';
            return {
                ...state,
                date,
                time
            };
        }
        default:
            return state;
    }
}
/**
 * This Date time picker has no knowledge about timezones. Date and time should be prepared to be
 * the values that are displayed to the user.
 * The value that is propagated with `onChange` will be formatted like: YYYY-MM-DDTHH:MM
 * The parent component is responsible to apply the timezone
 *
 * @param props
 * @constructor
 */
export function DateTimePicker(props) {
    const [state, dispatch] = useReducer(reducer, {
        date: extractDateFromISO(props.value || props.defaultValue),
        time: extractTimeFromISO(props.value || props.defaultValue)
    });
    function updateDateValue(date) {
        dispatch({ type: 'date', payload: { date } });
    }
    function updateTimeValue(time) {
        dispatch({ type: 'time', payload: { time } });
    }
    useEffect(() => {
        let date = props.value || props.defaultValue;
        if (!date) {
            date = new Date().toISOString();
        }
        dispatch({
            type: 'reset',
            payload: {
                date: extractDateFromISO(date),
                time: extractTimeFromISO(date)
            }
        });
    }, [props.value, props.defaultValue]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: onChange should not change
    useEffect(() => {
        var _a;
        if (state.datetime && state.datetime !== (props.value || props.defaultValue)) {
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, state.datetime);
        }
    }, [state.datetime]);
    const timeValues = [
        '00:00',
        '00:30',
        '01:00',
        '01:30',
        '02:00',
        '02:30',
        '03:00',
        '03:30',
        '04:00',
        '04:30',
        '05:00',
        '05:30',
        '06:00',
        '06:30',
        '07:00',
        '07:30',
        '08:00',
        '08:30',
        '09:00',
        '09:30',
        '10:00',
        '10:30',
        '11:00',
        '11:30',
        '12:00',
        '12:30',
        '13:00',
        '13:30',
        '14:00',
        '14:30',
        '15:00',
        '15:30',
        '16:00',
        '16:30',
        '17:00',
        '17:30',
        '18:00',
        '18:30',
        '19:00',
        '19:30',
        '20:00',
        '20:30',
        '21:00',
        '21:30',
        '22:00',
        '22:30',
        '23:00',
        '23:30'
    ];
    return (React.createElement(Inline, { space: 'space.100' },
        React.createElement(DatePicker, { id: props.id, weekStartDay: 1, dateFormat: DEFAULT_DATE_FORMAT, value: state.date, onChange: updateDateValue }),
        React.createElement(Box, { xcss: xcss({ minWidth: '75px' }) },
            React.createElement(TimePicker, { timeIsEditable: true, times: timeValues, timeFormat: DEFAULT_TIME_FORMAT, value: state.time, placeholder: '##.##', onChange: updateTimeValue }))));
}
